<template>
  <v-dialog v-model="value" max-width="800px" persistent>
    <v-card>
      <v-card-title
        ><span class="headline font-weight-thin" v-if="mode === 'edit'"
          >Edit SA {{ serviceArea.id }}: {{ serviceAreaName }}</span
        >
        <span class="headline font-weight-thin" v-if="mode === 'create'">Create a New Service Area</span>
      </v-card-title>
      <v-card-text>
        <v-form v-model="valid" ref="form">
          <v-checkbox
            v-if="mode === 'edit'"
            v-model="serviceArea.active"
            label="Active"
            data-cy="active-toggle"
          ></v-checkbox>
          <v-text-field
            :rules="nameRules"
            class="mt-3"
            clearable
            data-cy="serviceAreaName"
            label="Service Area Name"
            outlined
            ref="nameInput"
            required
            v-model="serviceArea.name"
          ></v-text-field>
          <div v-if="mode === 'create'">
            <v-text-field
              :rules="idRules"
              data-cy="serviceAreaId"
              label="Service Area ID"
              outlined
              type="number"
              v-model="serviceArea.id"
            ></v-text-field>
          </div>

          <v-autocomplete
            :items="dropdowns.careSetting"
            :rules="careSettingRules"
            data-cy="careSettingDropdown"
            outlined
            label="Care Setting"
            v-model="serviceArea.careSetting"
          ></v-autocomplete>

          <user-picker
            attributeName="accountManager"
            :outlined="true"
            label="Account Manager"
            v-model="serviceArea.accountManager"
          ></user-picker>

          <div v-if="mode === 'create'">
            <v-text-field
              :outlined="true"
              disabled
              label="Primary Contact"
              hint="Create the Service Area first, then edit to add contacts"
              persistent-hint
              value="(Available after creation)"
            ></v-text-field>
            <v-text-field
              :outlined="true"
              disabled
              label="Secondary Contact"
              hint="Create the Service Area first, then edit to add contacts"
              persistent-hint
              value="(Available after creation)"
              class="mt-3"
            ></v-text-field>
          </div>

          <div v-if="mode === 'edit'">
            <service-area-user-picker
              attributeName="primaryContact"
              :outlined="true"
              :serviceAreaId="serviceArea.id"
              label="Primary Contact"
              v-model="serviceArea.contactPrimary"
              class="mt-6"
            ></service-area-user-picker>
            <service-area-user-picker
              attributeName="secondaryContact"
              :outlined="true"
              :serviceAreaId="serviceArea.id"
              label="Secondary Contact"
              v-model="serviceArea.contactSecondary"
              class="mt-6"
            ></service-area-user-picker>
          </div>

          <generic-loader
            caption="Saving data and backfilling member test plan data..."
            topMargin="2"
            v-if="isSavingData && mode === 'create'"
          ></generic-loader>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn @click="close" data-cy="cancelButton" outlined small>Cancel</v-btn>
            <v-btn @click="save" :disabled="!valid" color="primary" data-cy="saveButton" outlined small>Save</v-btn>
          </v-card-actions>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";

import GenericLoader from "@/components/GenericLoader";
import UserPicker from "@/components/userPicker";
import ServiceAreaUserPicker from "@/components/ServiceAreaUserPicker";

export default {
  components: {
    GenericLoader,
    UserPicker,
    ServiceAreaUserPicker,
  },
  computed: mapState({
    serviceArea: state => state.admin.serviceArea.edit,
    dropdowns: state => state.dropdown.dropdowns,
  }),
  data() {
    return {
      idRules: [name => !!name || "ID is required", () => this.idDoesNotConflict() || "Service Area ID must be unique"],
      isSavingData: false,
      nameRules: [name => !!name || "Name is required"],
      careSettingRules: [name => !!name || "Care setting is required"],
      serviceAreaName: null,
      valid: true,
    };
  },
  methods: {
    checkForIdConflict() {
      const conflict = this.serviceAreaList.find(serviceArea => {
        const idMatch = `${serviceArea.id}`.trim() === `${this.serviceArea.id}`.trim().toUpperCase();

        return idMatch;
      });

      return conflict ? true : false;
    },
    close() {
      this.$emit("input", false);
    },
    idDoesNotConflict() {
      if (!this.serviceArea.id) return true;

      return this.checkForIdConflict() ? false : true;
    },
    resetCreateForm() {
      this.$refs.form.reset();
      this.$refs.form.resetValidation();
    },
    async save() {
      this.isSavingData = true;
      try {
        if (this.mode === "edit") {
          await this.updateServiceArea(this.serviceArea);
          this.$notifySuccess(`SA ${this.serviceArea.id} ${this.serviceArea.name} saved`);
        }

        if (this.mode === "create") {
          this.serviceArea.id = parseInt(this.serviceArea.id, 10);
          await this.createServiceArea(this.serviceArea);
          this.$notifySuccess(`SA ${this.serviceArea.id} ${this.serviceArea.name} created`);
        }
        this.close();
      } catch (e) {
        console.error(e);
        this.$notifyError(e);
      }
      this.isSavingData = false;
    },
    ...mapActions({
      createServiceArea: "admin/serviceArea/create",
      loadUserList: "user/load",
      updateServiceArea: "admin/serviceArea/update",
    }),
  },
  props: {
    mode: { default: "create", required: true, type: String },
    serviceAreaList: { type: Array },
    value: { required: true, type: Boolean },
  },
  watch: {
    value(val) {
      if (!val) {
        return;
      }

      if (this.mode === "create" && this.$refs.form) {
        this.resetCreateForm();
      }

      if (this.serviceArea.name) {
        this.serviceAreaName = this.serviceArea.name;
      }
    },
  },
};
</script>
